import { AppointmentSummary } from "@/types/AppointmentSummary";
import { IconCalendarEvent } from "@tabler/icons-react";
import { useNavigate as useRouter, useParams } from "react-router-dom";
import React from "react";
import type { AppointmentStatus } from "@/lib/slotting/types";
import { APPOINTMENT_STATUS_BADGE } from "@/lib/slotting/statusColors";
import { useEventTimezone } from "@/providers/EventProvider";

type TCCSlottingProps = {
  value: AppointmentSummary[];
  isEditing: boolean;
};

// startDt/endDt are real UTC instants — render the wall-clock in the event tz,
// not the admin's browser tz (otherwise the time/day shifts by the offset).
function formatAppointment(appt: AppointmentSummary, tz: string): string {
  const start = new Date(appt.startDt);
  const end = new Date(appt.endDt);
  const day = start.toLocaleString("en", { timeZone: tz, day: "numeric" });
  const month = start.toLocaleString("en", { timeZone: tz, month: "short" });
  const startTime = start.toLocaleTimeString("en", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const endTime = end.toLocaleTimeString("en", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day} ${month} ${startTime}–${endTime}`;
}

export const TCCSlotting: React.FC<TCCSlottingProps> = ({
  value,
  isEditing,
}) => {
  const router = useRouter();
  const { eventId = "" } = useParams<{ eventId: string }>();
  const tz = useEventTimezone();

  if (isEditing) return <></>;

  const appointments = value || [];

  if (appointments.length === 0) {
    return <></>;
  }

  const displayed = appointments.slice(0, 2);
  const remaining = appointments.length - 2;

  const handleClick = (appt: AppointmentSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const dateStr = appt.startDt.split("T")[0];
    router(
      `/event/${eventId}/slotting/${appt.offeringId}?appointmentId=${appt.appointmentId}&date=${dateStr}`
    );
  };

  return (
    <div className="flex flex-wrap gap-1">
      {displayed.map((appt) => {
        const colorClass =
          APPOINTMENT_STATUS_BADGE[appt.status as AppointmentStatus] ??
          APPOINTMENT_STATUS_BADGE.TENTATIVE;
        return (
          <span
            key={appt.appointmentId}
            onClick={(e) => handleClick(appt, e)}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-transparent ${colorClass} text-[10px] font-medium cursor-pointer hover:border hover:outline-tertiary hover:shadow-sm transition-all`}
          >
            <IconCalendarEvent size={12} />
            {formatAppointment(appt, tz)}
          </span>
        );
      })}
      {remaining > 0 && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md surface-tertiary body-3-medium font-secondary">
          +{remaining}
        </span>
      )}
    </div>
  );
};
